<template>
  <div v-loading="loading">
    <el-table
      :data="accounts"
      :stripe="true"
    >
      <el-table-column type="expand">
        <template slot-scope="props">
          <Peek :account="props.row" />
        </template>
      </el-table-column>

      <el-table-column
        prop="name"
        label="Name"
        sortable
        min-width="320px"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          <el-image
            v-if="scope.row.tracking_logo_link && false"
            style="width: 32px; height: 32px; margin-right: 8px"
            :src="getIconURLFromDomain(scope.row.tracking_logo_link)"
          />
          <router-link :to="{ name:'manage-accounts-account', params: { accountId: scope.row.id } }">
            {{ scope.row.name }}
          </router-link>
          <FontAwesomeIcon
            v-if="scope.row.is_parent"
            :icon="['fa', 'home']"
            class="pr-1"
            fixed-width
          />
          <el-tag
            v-if="scope.row.is_open360"
            size="mini"
          >
            Open360
          </el-tag>
          <el-tag
            v-if="isManaged(scope.row)"
            size="mini"
            type="warning"
            class="ml-1"
          >
            SaaS
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        label="Billing Code"
        prop="billing_myob_id"
        sortable
        width="140px"
        show-overflow-tooltip
      />

      <el-table-column
        label="Portfolio"
        prop="portfolio"
        sortable
        width="240px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue ? cellValue.long : '-'"
      />

      <el-table-column
        label="Billing Group"
        width="240px"
        show-overflow-tooltip
        :formatter="row => $_.get(row, 'b_group.long', '-')"
      />

      <el-table-column
        prop="crm_endangered"
        label="Standing"
        align="center"
        sortable
        width="120px"
      >
        <template v-slot="{ row }">
          <el-tag
            :type="getStandingTagType(row.crm_endangered)"
            size="mini"
          >
            {{ getStandingDescription(row.crm_endangered) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        prop="crm_account_grade"
        label="Grade"
        sortable
        align="center"
        width="120px"
      >
        <template v-slot="{ row }">
          <el-tag
            :type="getGradeTagType($_.get(row, 'crm_account_grade'))"
            size="mini"
          >
            {{ getGradeDescription($_.get(row, 'crm_account_grade')) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        prop="status"
        label="Status"
        align="center"
        sortable
        width="120px"
      >
        <template v-slot="{ row }">
          <el-tag
            :type="getStatusTagType($_.get(row, 'status'))"
            size="mini"
          >
            {{ $_.get(row, 'status') | titleize }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>
    <!-- Pagination -->
    <br>
    <div style="text-align: center;">
      <el-pagination
        :current-page.sync="accounts.page"
        :page-size.sync="accounts.limit"
        layout="total, sizes, prev, pager, next, jumper"
        :total="accounts.total"
        @size-change="reload"
        @current-change="reload"
      />
    </div>
  </div>
</template>

<script>
import Peek from '@/components/Application/Manage/Accounts/Peek'
import Filter, { IS } from '@/plugins/sdk/Filter'

export default {
  components: {
    Peek
  },
  props: {
    filters: {
      type: Array,
      required: true
    },
    sort: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      accounts: [],
      loading: false,
      page: 1,
      perPage: 20,
      total: 0,
      crmAccountTypes: [],
      portfolios: [],
      isLoadingPortfolios: false,
      actions: []
    }
  },
  created () {
    this.configuration = this.$_.cloneDeep(this.$store.getters.configuration)
    if (!this.$_.isUndefined(this.configuration.layout.activeAccountDefault) && this.configuration.layout.activeAccountDefault) {
      this.filters.push(new Filter('status', IS, 'trading'))
    }
  },
  methods: {
    isManaged (account) {
      return this.$_.get(account, 'b_group.short', '-') === 'OFS'
    },
    getIconURLFromDomain (domain) {
      const d = new URL(domain) // validate domain
      console.log(`https://icon.horse/icon/${d.hostname}`)
      return `https://icon.horse/icon/${d.hostname}`
    },
    loadTable () {
      this.loading = true
      this.$sdk.account.list((builder) => {
        builder.page = 1
        builder.limit = 20
        builder.include = ['portfolio.profile', 'b_group']
        builder.order = `${this.sort.field}:${this.sort.direction}`
        builder.filters = this.filters
      }).then(res => {
        this.accounts = res
      }).finally(() => { this.loading = false })
    },
    async reload () {
      try {
        this.loading = true
        await this.accounts.reload()
      } catch {
        this.$message.error('An error occurred while loading the accounts')
      } finally {
        this.loading = false
      }
    },
    isSaaSAccount (account) {
      return account.b_group && account.b_group.short === 'OFS'
    },
    getStatusTagType (status) {
      switch (status) {
        case 'on hold':
          return 'danger'
        default:
          return 'info'
      }
    },
    getGradeTagType (grade) {
      switch (grade) {
        case 1:
        case 6:
          return 'success'
        case 2:
        case 3:
          return 'warning'
        case 4:
          return 'danger'
        case 5:
        default:
          return 'info'
      }
    },
    getStandingTagType (standing) {
      switch (standing) {
        case -1:
          return 'success'
        case 1:
          return 'danger'
        default:
          return 'info'
      }
    },
    getStandingDescription (standing) {
      switch (standing) {
        case -1:
          return 'New'
        case 1:
          return 'Endangered'
        default:
          return 'Healthy'
      }
    },
    getGradeDescription (grade) {
      switch (grade) {
        case 1:
          return 'A'
        case 2:
          return 'B'
        case 3:
          return 'C'
        case 4:
          return 'D'
        case 6:
          return 'HC'
        case 5:
        default:
          return 'No Trade'
      }
    }
  }
}
</script>

<style scoped lang="scss">
</style>
